'use client'

import Link from 'next/link'
import { useEffect, useState } from 'react'
import { useRouter } from 'next/navigation'
import { createClient } from '@/lib/supabase/client'
import type { DBRole } from '@/lib/auth-context'

/**
 * POST-LOGIN LANDING, BY ROLE
 *
 * Every successful sign-in used to push to /portal/dashboard. That is the right
 * answer for a project developer and the wrong one for the two roles that have
 * a workspace of their own:
 *
 *   VVB_AUDITOR          /vvb/dashboard, /vvb/validations, /vvb/verifications,
 *                        /vvb/coi — a complete, role-gated workspace that
 *                        NOTHING in the codebase linked to. An auditor signing
 *                        in landed on the developer portal and had no way to
 *                        discover their own section short of typing the URL.
 *   METHODOLOGY_REVIEWER /reviewer/dashboard — reachable only the same way.
 *
 * MECHANISM: the same one the rest of the codebase uses, deliberately not a new
 * one. The authority is `public.users.role`, a role_enum value. lib/auth-server
 * `requireRole()` reads it server-side for the /reviewer and /vvb pages;
 * lib/auth-context `loadUserProfile()` reads it client-side with
 * `.from('users').select('role').eq('id', user.id)` and hands it to the portal
 * layouts as `user.dbRole`. This page is a client component that is NOT inside
 * <Providers>, so `useAuth()` is unavailable here — the AuthProvider is mounted
 * per-portal-layout, not in the root layout. The read below is therefore made
 * directly against the same table and column, and the DBRole type is imported
 * from auth-context so the two cannot drift apart.
 *
 * WHY EXACT ROLES AND NOT REVIEWER_ROLES / VVB_ROLES
 *
 * Those sets (lib/auth-server.ts) exist to answer "may this user enter?" and
 * both include PROGRAMME_ADMINISTRATOR and SYSTEM_ADMINISTRATOR. Access is not
 * the question here — landing is. An administrator is admitted to both
 * workspaces but works out of /portal/admin, and routing them into a VVB queue
 * on every sign-in would be wrong for the same reason routing an auditor to the
 * developer portal is. So this maps only the two roles whose day starts in
 * their own workspace and lets everything else, admins included, keep the
 * existing /portal/dashboard landing.
 *
 * FAILURE MODE: if the profile row is missing or the read fails under RLS the
 * function returns the unchanged /portal/dashboard. Landing a VVB auditor on
 * the general portal is the status quo and is recoverable in one click; the
 * alternative — guessing a workspace from the email domain or from
 * user_metadata, neither of which is authoritative — is not.
 */
const LANDING_BY_ROLE: Partial<Record<DBRole, string>> = {
  VVB_AUDITOR: '/vvb/dashboard',
  METHODOLOGY_REVIEWER: '/reviewer/dashboard',
}

const DEFAULT_LANDING = '/portal/dashboard'

async function landingPathForUser(
  supabase: ReturnType<typeof createClient>,
  userId: string,
): Promise<string> {
  const { data } = await supabase
    .from('users')
    .select('role')
    .eq('id', userId)
    .maybeSingle()

  const role = (data as { role?: DBRole } | null)?.role
  return (role && LANDING_BY_ROLE[role]) || DEFAULT_LANDING
}

export default function Login() {
  const router = useRouter()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [rememberMe, setRememberMe] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  // MFA challenge step: set when the password was right but the account has a
  // verified authenticator — the session stays AAL1 until the code verifies.
  const [mfaFactorId, setMfaFactorId] = useState<string | null>(null)
  const [mfaCode, setMfaCode] = useState('')

  const handleMfaVerify = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!mfaFactorId || mfaCode.trim().length < 6) return
    setError(null)
    setIsLoading(true)
    try {
      const supabase = createClient()
      const { error: vErr } = await supabase.auth.mfa.challengeAndVerify({ factorId: mfaFactorId, code: mfaCode.trim() })
      if (vErr) {
        setError('That code was not accepted — check your authenticator app and try again.')
        return
      }
      const { data: { user: u } } = await supabase.auth.getUser()
      const landing = u ? await landingPathForUser(supabase, u.id) : DEFAULT_LANDING
      router.push(landing)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  // Surface OAuth-callback errors (?error=...) redirected back to this page.
  useEffect(() => {
    const err = new URLSearchParams(window.location.search).get('error')
    if (err) setError(err === 'missing_code' ? 'Sign-in was cancelled or failed. Please try again.' : err)
  }, [])

  // OAuth history: removed 01.09.2026 because the buttons offered providers
  // nobody had configured (every click ended in "Unsupported provider").
  // Google restored 02.09.2026 the same hour the owner created the OAuth
  // client and enabled the provider in Supabase — the button ships only
  // because the provider is live. Microsoft stays out until someone
  // configures it. Note for the security review: an OAuth session lands at
  // AAL1; users with a verified authenticator are stepped up by the
  // server-side assertion on decision routes, and a dedicated post-OAuth
  // challenge screen is a noted follow-up.
  const handleOAuth = async (provider: 'google') => {
    setError(null)
    setIsLoading(true)
    try {
      const supabase = createClient()
      const { error: oauthError } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: `${window.location.origin}/auth/callback?next=/portal/dashboard`,
        },
      })
      if (oauthError) {
        setError(oauthError.message)
        setIsLoading(false)
      }
      // On success the browser navigates away to the provider.
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign-in failed. Please try again.')
      setIsLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!email || !password) {
      setError('Please enter both email and password')
      return
    }

    setIsLoading(true)
    try {
      const supabase = createClient()
      const { data, error: authError } = await supabase.auth.signInWithPassword({ email, password })
      if (authError) {
        setError(authError.message)
      } else {
        // MFA: if the account has a verified authenticator, the password step
        // only reaches AAL1 — ask for the six-digit code before letting the
        // session into the portal.
        const { data: aal } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel()
        if (aal && aal.nextLevel === 'aal2' && aal.nextLevel !== aal.currentLevel) {
          const { data: factorData } = await supabase.auth.mfa.listFactors()
          const totp = (factorData?.totp ?? []).find(f => f.status === 'verified')
          if (totp) {
            setMfaFactorId(totp.id)
            setIsLoading(false)
            return
          }
        }
        // See LANDING_BY_ROLE above. The session cookie is set by the call
        // above, so this read runs as the signed-in user and is subject to the
        // same RLS policy the portal layouts read `users` under.
        const landing = data.user
          ? await landingPathForUser(supabase, data.user.id)
          : DEFAULT_LANDING
        router.push(landing)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, var(--mcc-deep) 0%, var(--mcc-navy) 40%, var(--mcc-navy) 70%, var(--mcc-surface) 100%)', fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif", padding: '24px' }}>
      <div style={{ width: '100%', maxWidth: '440px' }}>
        {/* Logo & Header */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <Link href="/" style={{ textDecoration: 'none' }}>
            <div style={{ width: '64px', height: '64px', backgroundColor: 'var(--mcc-navy)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', boxShadow: '0 8px 32px rgba(10,46,77,0.3)' }}>
              <span style={{ color: 'white', fontWeight: 800, fontSize: '24px', letterSpacing: '-0.5px' }}>MCC</span>
            </div>
          </Link>
          <h1 style={{ fontSize: '24px', fontWeight: 600, color: 'white', marginBottom: '4px', letterSpacing: '-0.3px' }}>Marine Conservation Credits</h1>
          <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.6)' }}>Registry & Crediting Platform</p>
        </div>

        {/* Login Card */}
        <div style={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 12px 40px rgba(11,25,41,0.16)', overflow: 'hidden' }}>
          <div style={{ padding: '36px 36px 12px' }}>
            <h2 style={{ fontSize: '26px', fontWeight: 600, color: 'var(--mcc-navy)', marginBottom: '6px' }}>{mfaFactorId ? 'Two-factor code' : 'Sign In'}</h2>
            <p style={{ fontSize: '15px', color: 'var(--mcc-muted)', marginBottom: '28px' }}>
              {mfaFactorId
                ? 'Enter the six-digit code from your authenticator app to finish signing in.'
                : 'Access your MCC account to manage conservation credits'}
            </p>

            {mfaFactorId ? (
              <form onSubmit={handleMfaVerify}>
                {error && (
                  <div style={{ padding: '14px 16px', borderRadius: '8px', backgroundColor: 'var(--mcc-danger-bg)', border: '1px solid #fecaca', marginBottom: '20px' }}>
                    <p style={{ fontSize: '14px', fontWeight: 500, color: 'var(--mcc-danger-fg)', margin: 0 }}>{error}</p>
                  </div>
                )}
                <input
                  autoFocus
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
                  inputMode="numeric"
                  placeholder="123456"
                  style={{ width: '100%', padding: '14px 16px', fontSize: '22px', letterSpacing: '0.3em', textAlign: 'center', fontFamily: 'ui-monospace, monospace', border: '2px solid var(--mcc-border)', borderRadius: '8px', outline: 'none', boxSizing: 'border-box', marginBottom: '20px' }}
                />
                <button
                  type="submit"
                  disabled={isLoading || mfaCode.length < 6}
                  style={{ width: '100%', padding: '14px', backgroundColor: 'var(--mcc-navy)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: 600, cursor: isLoading ? 'wait' : 'pointer', opacity: mfaCode.length < 6 ? 0.6 : 1, marginBottom: '16px' }}
                >
                  {isLoading ? 'Verifying…' : 'Verify and sign in'}
                </button>
                <button
                  type="button"
                  onClick={() => { setMfaFactorId(null); setMfaCode(''); setError(null) }}
                  style={{ width: '100%', padding: '10px', background: 'none', border: 'none', color: 'var(--mcc-muted)', fontSize: '14px', cursor: 'pointer', marginBottom: '12px' }}
                >
                  Back to sign in
                </button>
              </form>
            ) : (
            <form onSubmit={handleSubmit}>
              {/* Error */}
              {error && (
                <div style={{ padding: '14px 16px', borderRadius: '8px', backgroundColor: 'var(--mcc-danger-bg)', border: '1px solid #fecaca', marginBottom: '20px' }}>
                  <p style={{ fontSize: '14px', fontWeight: 500, color: 'var(--mcc-danger-fg)', margin: 0 }}>{error}</p>
                </div>
              )}

              {/* Email */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: 'var(--mcc-ink)', marginBottom: '8px' }}>
                  Email Address
                </label>
                <input
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading}
                  required
                  style={{ width: '100%', padding: '12px 16px', borderRadius: '8px', border: error ? '2px solid var(--mcc-danger-fg)' : '2px solid var(--mcc-border)', fontSize: '15px', outline: 'none', transition: 'border-color 0.2s', backgroundColor: isLoading ? 'var(--mcc-surface)' : 'white', boxSizing: 'border-box' }}
                  onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--mcc-navy)'; }}
                  onBlur={(e) => { e.currentTarget.style.borderColor = error ? 'var(--mcc-danger-fg)' : 'var(--mcc-border)'; }}
                />
              </div>

              {/* Password */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: 'var(--mcc-ink)', marginBottom: '8px' }}>
                  Password
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={isLoading}
                    required
                    style={{ width: '100%', padding: '12px 48px 12px 16px', borderRadius: '8px', border: error ? '2px solid var(--mcc-danger-fg)' : '2px solid var(--mcc-border)', fontSize: '15px', outline: 'none', transition: 'border-color 0.2s', backgroundColor: isLoading ? 'var(--mcc-surface)' : 'white', boxSizing: 'border-box' }}
                    onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--mcc-navy)'; }}
                    onBlur={(e) => { e.currentTarget.style.borderColor = error ? 'var(--mcc-danger-fg)' : 'var(--mcc-border)'; }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={isLoading}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--mcc-muted)', fontSize: '14px', padding: '4px' }}
                  >
                    {showPassword ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                    )}
                  </button>
                </div>
              </div>

              {/* Remember Me */}
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '24px' }}>
                <input
                  type="checkbox"
                  id="rememberMe"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  disabled={isLoading}
                  style={{ width: '18px', height: '18px', accentColor: 'var(--mcc-navy)', cursor: 'pointer', marginRight: '10px', flexShrink: 0 }}
                />
                <label htmlFor="rememberMe" style={{ fontSize: '14px', color: 'var(--mcc-muted)', cursor: 'pointer' }}>
                  Remember me for 30 days
                </label>
              </div>

              {/* Sign In Button */}
              <button
                type="submit"
                disabled={isLoading}
                style={{
                  width: '100%',
                  padding: '14px',
                  borderRadius: '8px',
                  border: 'none',
                  backgroundColor: isLoading ? 'var(--mcc-faint)' : 'var(--mcc-navy)',
                  color: 'white',
                  fontSize: '16px',
                  fontWeight: 700,
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  transition: 'background-color 0.2s',
                  boxShadow: '0 4px 12px rgba(10,46,77,0.25)',
                }}
                onMouseEnter={(e) => { if (!isLoading) e.currentTarget.style.backgroundColor = 'var(--mcc-navy)'; }}
                onMouseLeave={(e) => { if (!isLoading) e.currentTarget.style.backgroundColor = 'var(--mcc-navy)'; }}
              >
                {isLoading ? 'Signing in...' : 'Sign In'}
              </button>

              {/* OAuth — Google only; the provider is configured and live. */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '20px 0 16px' }}>
                <div style={{ flex: 1, height: 1, background: 'var(--mcc-border)' }} />
                <span style={{ fontSize: 12, color: 'var(--mcc-muted)', fontWeight: 500 }}>or continue with</span>
                <div style={{ flex: 1, height: 1, background: 'var(--mcc-border)' }} />
              </div>
              <button
                type="button"
                onClick={() => handleOAuth('google')}
                disabled={isLoading}
                style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '12px 16px', borderRadius: '8px', border: '1px solid var(--mcc-border)', background: '#fff', fontSize: 14, fontWeight: 600, color: 'var(--mcc-ink)', cursor: isLoading ? 'not-allowed' : 'pointer' }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15A11 11 0 0 0 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" />
                </svg>
                Sign in with Google
              </button>
            </form>
            )}

          </div>

          {/* Divider + Links */}
          <div style={{ borderTop: '1px solid var(--mcc-border)', padding: '24px 36px', display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'center' }}>
            <Link href="/auth/forgot-password" style={{ textDecoration: 'none', fontSize: '14px', color: 'var(--mcc-navy)', fontWeight: 600 }}>
              Forgot password?
            </Link>
            <p style={{ fontSize: '14px', color: 'var(--mcc-muted)', margin: 0 }}>
              Don&apos;t have an account?{' '}
              <Link href="/auth/register" style={{ textDecoration: 'none', color: 'var(--mcc-sea)', fontWeight: 600 }}>
                Create account
              </Link>
            </p>
          </div>
        </div>

        {/* Footer */}
        <div style={{ textAlign: 'center', marginTop: '32px' }}>
          <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.5)', margin: 0 }}>
            Marine Conservation Credits — Registry & Crediting Platform
          </p>
          <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.35)', marginTop: '8px' }}>
            &copy; 2026 Marine Conservation Credits. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  )
}
